import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { lifecycleWrite, settleWrite } from '../api/write-keys';
import { useSession } from '../auth/session';
import { Alert, Button, Chip, Empty, Field, Money, Note, PageHeader, Panel, Spinner, Table, Tabs, Tiles, type Tone } from '../ui';

/**
 * A-27 Account wind-down & closure — AL-01…AL-05.
 *
 * One route, `/wind-down`, with the account in `?retailer=`. A-04 links here for any account,
 * which is how a wind-down BEGINS; the list shows every account winding down or closed, which is
 * how one is followed to closure and, afterwards, found again (AL-05).
 *
 * The verdict is the server's. `canClose` and the blockers come from the API's AL-03 function;
 * this screen renders them and never re-decides — a second copy of the rule in the browser is
 * the copy that would drift. The Close button is disabled when the API says no, and the API
 * refuses again under lock if the position changed in between.
 *
 * Wind-down stops ORDERING only. The retailer keeps signing in, reporting sales and requesting
 * returns, because AL-01 requires its held pieces to be sold or returned — the screen says so,
 * so nobody winding an account down expects its people to be locked out.
 */

type Status = 'ACTIVE' | 'ON_HOLD' | 'DORMANT' | 'WIND_DOWN' | 'CLOSED';
type BlockerCode = 'NOT_WINDING_DOWN' | 'CLOSED' | 'PIECES_OUT' | 'LOSS_OPEN' | 'PAYABLE_OWED' | 'CREDIT_HELD' | 'OPEN_ORDERS';
interface Amount { currency: string; amount: string }
interface Blocker { code: BlockerCode; count?: number; amounts?: Amount[] }
interface Group { count: number; byStatus: Record<string, number>; wholesale: Amount[] }
interface Summary {
  id: string; name: string; city: string | null; countryCode: string; tradingCurrency: string;
  status: Status; statusReason: string | null; statusChangedAt: string | null;
  closedAt: string | null; closureReason: string | null;
}
interface ListRow extends Summary { piecesOut: number; openOrders: number; payable: Amount[]; canClose: boolean; blockers: BlockerCode[] }
interface Detail extends Summary {
  windDownBeganFrom: Status | null;
  position: {
    openOrders: Array<{ id: string; reference: string; status: string; submittedAt: string | null }>;
    pieces: { out: Group; lossOpen: Group; sold: Group; done: number };
    payable: Amount[];
    subscription: { status: string; renewsOn: string | null; feeUnpaid: Amount | null } | null;
    activeUsers: number;
    blockers: Blocker[];
    canClose: boolean;
  };
}

const TONE: Record<Status, Tone> = { ACTIVE: 'done', ON_HOLD: 'stop', DORMANT: 'lapse', WIND_DOWN: 'run', CLOSED: 'them' };
const day = (iso: string | null) => (iso ? iso.slice(0, 10) : '—');

function StatusChip({ status }: { status: Status }) {
  const { t } = useTranslation();
  return <Chip tone={TONE[status]}>{t(`windDown.status.${status}`)}</Chip>;
}

function Amounts({ list }: { list: readonly Amount[] }) {
  const { t } = useTranslation();
  if (list.length === 0) return <span className="mk-dim">{t('windDown.nothing')}</span>;
  return <>{list.map((a) => <div key={a.currency}><Money amount={a.amount} currency={a.currency} /></div>)}</>;
}

export function AccountWindDown() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const selected = params.get('retailer');
  const [tab, setTab] = useState<'ALL' | 'WIND_DOWN' | 'CLOSED'>('ALL');
  const { portal } = useSession();
  // The portal is in every key: after a portal switch, a cached list must not render the other
  // portal's accounts while the new one loads (SC-02).
  const { data, isLoading, error } = useQuery({
    queryKey: ['portal', portal?.code ?? null, 'lifecycle'], queryFn: () => api.get<{ accounts: ListRow[] }>('/lifecycle/retailers'),
  });

  const rows = (data?.accounts ?? []).filter((r) => tab === 'ALL' || r.status === tab);
  const count = (s: Status) => String((data?.accounts ?? []).filter((r) => r.status === s).length);

  return (
    <>
      <PageHeader title={t('windDown.title')} />
      {selected ? <Position retailerId={selected} onBack={() => setParams({})} /> : null}
      <Panel title={t('windDown.listTitle')}>
        <Tabs
          tabs={[
            { id: 'ALL', label: t('windDown.tabAll') },
            { id: 'WIND_DOWN', label: t('windDown.status.WIND_DOWN'), badge: count('WIND_DOWN') },
            { id: 'CLOSED', label: t('windDown.status.CLOSED'), badge: count('CLOSED') },
          ]}
          active={tab}
          onChange={(id) => setTab(id as typeof tab)}
        />
        {isLoading ? <Spinner /> : error ? <Alert tone="stop">{error.message}</Alert> : (
          <Table
            rows={rows}
            rowKey={(r) => r.id}
            columns={[
              {
                header: t('windDown.colAccount'),
                render: (r) => (
                  <>
                    <Link to={`/wind-down?retailer=${r.id}`}>{r.name}</Link>
                    <div className="mk-hint">{r.city ?? '—'}, {r.countryCode}</div>
                  </>
                ),
              },
              { header: t('windDown.colStatus'), render: (r) => <StatusChip status={r.status} /> },
              { header: t('windDown.colSince'), render: (r) => <span className="mk-dim">{day(r.closedAt ?? r.statusChangedAt)}</span> },
              { header: t('windDown.colPiecesOut'), numeric: true, render: (r) => r.piecesOut },
              { header: t('windDown.colOpenOrders'), numeric: true, render: (r) => r.openOrders },
              { header: t('windDown.colPayable'), numeric: true, render: (r) => <Amounts list={r.payable} /> },
              {
                header: t('windDown.colClosure'),
                render: (r) => r.status === 'CLOSED' ? <span className="mk-dim">{t('windDown.closedOn', { day: day(r.closedAt) })}</span>
                  : r.canClose ? <Chip tone="done">{t('windDown.ready')}</Chip>
                  : <Chip tone="stop">{t('windDown.blockedBy', { n: r.blockers.length })}</Chip>,
              },
            ]}
            empty={<Empty title={t('windDown.emptyTitle')} body={t('windDown.emptyBody')} />}
          />
        )}
      </Panel>
    </>
  );
}

function BlockerLine({ b }: { b: Blocker }) {
  const { t } = useTranslation();
  return (
    <li>
      {t(`windDown.blocker.${b.code}`, { n: b.count ?? 0 })}
      {b.amounts && b.amounts.length > 0 ? <> — <Amounts list={b.amounts} /></> : null}
    </li>
  );
}

function Position({ retailerId, onBack }: { retailerId: string; onBack: () => void }) {
  const { t } = useTranslation();
  const { can, portal } = useSession();
  const qc = useQueryClient();
  const key = ['portal', portal?.code ?? null, 'lifecycle', retailerId];
  const { data, isLoading, error } = useQuery({
    queryKey: key, queryFn: () => api.get<Detail>(`/lifecycle/retailers/${retailerId}`),
  });
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function act(path: string) {
    setFailure(null);
    setBusy(true);
    try {
      await api.post(`/lifecycle/retailers/${retailerId}/${path}`, { reason: reason.trim() });
      setReason('');
      /*
       * A wind-down step changes figures all over the portal, so the whole portal subtree is
       * re-read — but only this screen's own lifecycle queries are waited for. The old await of
       * `['portal', code]` included the admin shell's "Waiting for your approval" banner, which
       * is mounted and polled on every page: a slow poll there held this button disabled over a
       * step the server had already taken. `write-keys.ts` has the full account.
       */
      await settleWrite(qc, lifecycleWrite(portal?.code ?? null));
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : t('windDown.failed'));
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!data) return null;
  const p = data.position;
  const manage = can('retailer.close');
  const reasonOk = reason.trim().length >= 3;
  const byStatus = (g: Group) => Object.entries(g.byStatus).map(([s, n]) => t('windDown.pieceCount', { n, status: t(`windDown.piece.${s}`) })).join(' · ');

  return (
    <Panel
      title={data.name}
      pad
      actions={<Button size="sm" tone="ghost" onClick={onBack}>{t('windDown.backToList')}</Button>}
    >
      <p>
        <StatusChip status={data.status} />{' '}
        {data.status === 'WIND_DOWN' ? t('windDown.windingSince', { day: day(data.statusChangedAt) }) : null}
        {data.status === 'CLOSED' ? t('windDown.closedOn', { day: day(data.closedAt) }) : null}
        {' '}<Link to={`/retailers/${data.id}`}>{t('windDown.openRecord')}</Link>
      </p>
      {data.status === 'WIND_DOWN' && data.statusReason ? <p className="mk-hint">{data.statusReason}</p> : null}
      {data.windDownBeganFrom && data.windDownBeganFrom !== 'ACTIVE'
        ? <Note>{t('windDown.beganFrom', { status: t(`windDown.status.${data.windDownBeganFrom}`) })}</Note> : null}

      {data.status === 'CLOSED' ? (
        <Alert tone="them">
          <b>{t('windDown.closedTitle')}</b> {data.closureReason}
          <div>{t('windDown.closedBody')}</div>
        </Alert>
      ) : data.status === 'WIND_DOWN' ? (
        <Note>{t('windDown.whatStops')}</Note>
      ) : null}

      <Tiles items={[
        { k: t('windDown.openOrders'), v: p.openOrders.length },
        { k: t('windDown.piecesOut'), v: p.pieces.out.count, s: <Amounts list={p.pieces.out.wholesale} /> },
        { k: t('windDown.payable'), v: <Amounts list={p.payable} />, s: t('windDown.payableHint') },
        ...(p.subscription ? [{
          k: t('windDown.subscription'), v: t(`subscriptions.status.${p.subscription.status}`),
          s: p.subscription.feeUnpaid
            ? <>{t('windDown.feeUnpaid')} <Money amount={p.subscription.feeUnpaid.amount} currency={p.subscription.feeUnpaid.currency} /></>
            : p.subscription.renewsOn ? t('windDown.renewsOn', { day: p.subscription.renewsOn }) : undefined,
        }] : []),
        { k: t('windDown.logins'), v: p.activeUsers },
      ]} />

      {p.pieces.out.count > 0 ? <p className="mk-hint">{t('windDown.outDetail')} {byStatus(p.pieces.out)}</p> : null}
      {p.pieces.sold.count > 0 ? <p className="mk-hint">{t('windDown.soldDetail', { n: p.pieces.sold.count })}</p> : null}

      {data.status !== 'CLOSED' ? (
        p.canClose ? <Alert tone="done">{t('windDown.canClose')}</Alert> : (
          <Alert tone="stop">
            <b>{t('windDown.cannotClose')}</b>
            <ul>{p.blockers.map((b) => <BlockerLine key={b.code} b={b} />)}</ul>
          </Alert>
        )
      ) : null}

      {p.openOrders.length > 0 ? (
        <Table
          rows={p.openOrders}
          rowKey={(o) => o.id}
          columns={[
            { header: t('windDown.colOrder'), render: (o) => <Link to={`/orders/${o.id}`}>{o.reference}</Link> },
            { header: t('windDown.colOrderStatus'), render: (o) => <span className="mk-dim">{o.status.replace(/_/g, ' ').toLowerCase()}</span> },
            { header: t('windDown.colSubmitted'), render: (o) => <span className="mk-dim">{day(o.submittedAt)}</span> },
          ]}
        />
      ) : null}

      {manage && data.status !== 'CLOSED' ? (
        <>
          <Field label={t('windDown.reason')} hint={t('windDown.reasonHint')}>
            <input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          {failure ? <Alert tone="stop">{failure}</Alert> : null}
          {data.status === 'WIND_DOWN' ? (
            <p>
              <Button size="sm" tone="danger" disabled={!p.canClose || !reasonOk || busy} onClick={() => void act('close')}>
                {t('windDown.close')}
              </Button>{' '}
              <Button size="sm" tone="ghost" disabled={!reasonOk || busy} onClick={() => void act('wind-down/cancel')}>
                {t('windDown.cancel')}
              </Button>
            </p>
          ) : (
            <p>
              <Button size="sm" disabled={!reasonOk || busy} onClick={() => void act('wind-down')}>{t('windDown.begin')}</Button>
              <span className="mk-hint"> {t('windDown.beginHint')}</span>
            </p>
          )}
        </>
      ) : null}
    </Panel>
  );
}
